import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Building2, Users, TrendingUp, Shield, Activity,
  Heart, Wind, Scale, Check, Download, Mail, Sparkles, Target, Award,
  Pencil, Save, X, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const SLUG = "longevity-corporate";

const T_STYLE = {
  cream: "#F5F1EB",
  forest: "#0F0F0F",
  gold: "#EC6B1F",
  goldSoft: "#F5842B",
  ink: "#0F0F0F",
  softInk: "#4a4a4a",
  line: "#E8E2D5",
};

const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

const ICON_MAP = {
  trending: TrendingUp, users: Users, heart: Heart, shield: Shield, target: Target, award: Award,
  activity: Activity, scale: Scale, wind: Wind,
};

// Inline editable text component
const IE = ({ editing, value, onChange, as = "span", className = "", multiline = false, style = {}, placeholder = "" }) => {
  if (!editing) {
    const Tag = as;
    return <Tag className={className} style={style}>{value || placeholder}</Tag>;
  }
  if (multiline) {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-orange-500`} style={style} rows={3} placeholder={placeholder} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-orange-500`} style={style} placeholder={placeholder} />;
};

const LongevityCorporatePage = () => {
  const { user, token } = useAuth();
  const isAdmin = user && ["admin", "direzione"].includes(user.role);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/landing/${SLUG}`).then(r => setData(r.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const set = (path, val) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.includes("[")) {
          const [name, idx] = k.split(/[\[\]]/).filter(Boolean);
          ref = ref[name][idx];
        } else ref = ref[k];
      }
      const last = keys[keys.length - 1];
      if (last.includes("[")) {
        const [name, idx] = last.split(/[\[\]]/).filter(Boolean);
        ref[name][idx] = val;
      } else ref[last] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Salvato");
      setEditing(false);
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare i contenuti di default?")) return;
    try {
      const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data); toast.success("Ripristinato"); setEditing(false);
    } catch { toast.error("Errore ripristino"); }
  };

  const uploadImage = async (fieldPath) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const img = new Image(); const reader = new FileReader();
      reader.onload = (ev) => { img.src = ev.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSide = 1800;
        let { width, height } = img;
        if (Math.max(width, height) > maxSide) {
          if (width >= height) { height = Math.round(height * (maxSide / width)); width = maxSide; }
          else { width = Math.round(width * (maxSide / height)); height = maxSide; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        set(fieldPath, canvas.toDataURL("image/jpeg", 0.85));
        toast.success("Immagine caricata");
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T_STYLE.cream, ...sans }}>Caricamento…</div>;

  const h = data.hero || {};
  const b = data.benefici || { items: [] };
  const pil = data.pilastri || { items: [] };
  const fs = data.fasi || { items: [] };
  const ch = data.coach || { items: [] };
  const nat = data.natura || {};
  const pk = data.pacchetti || { items: [] };
  const cta = data.cta_finale || {};
  const ft = data.footer || {};

  return (
    <div style={{ backgroundColor: T_STYLE.cream, color: T_STYLE.ink, ...sans }} data-testid="longevity-corporate-page">

      {/* ADMIN TOOLBAR */}
      {isAdmin && (
        <div className="sticky top-0 z-[70] px-4 py-2 flex items-center justify-between flex-wrap gap-2" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream, borderBottom: `2px solid ${T_STYLE.gold}` }} data-testid="admin-toolbar">
          <div className="flex items-center gap-2 text-xs">
            <span style={{ color: T_STYLE.goldSoft }} className="font-semibold tracking-wider uppercase">Admin</span>
            <span className="opacity-70">Longevity — Corporate</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!editing ? (
              <Button size="sm" onClick={() => setEditing(true)} style={{ backgroundColor: T_STYLE.gold, color: "#fff" }} data-testid="btn-edit"><Pencil className="w-4 h-4 mr-1" /> Modifica</Button>
            ) : (
              <>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Salva"}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900" data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Annulla</Button>
                <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600" data-testid="btn-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset default</Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <div className="w-full px-4 md:px-14 py-3 md:py-4 flex items-center justify-between gap-2 border-b" style={{ borderColor: T_STYLE.line, backgroundColor: T_STYLE.cream }}>
        <a href="/longevity" className="inline-flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs uppercase tracking-[2px] md:tracking-[3px]" style={{ color: T_STYLE.softInk }} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Longevity Academy</span><span className="sm:hidden">Home</span>
        </a>
        <div className="text-[10px] md:text-xs tracking-[2px] md:tracking-[4px] uppercase font-semibold text-center flex-1 md:flex-none" style={{ color: T_STYLE.gold }}>Corporate</div>
        <a href="/longevity/presentazione-banche" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[2px] px-4 py-2 border" style={{ borderColor: T_STYLE.gold, color: T_STYLE.forest }} data-testid="btn-download-brochure">
          <Download className="w-4 h-4" /> Brochure PDF
        </a>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: T_STYLE.forest }} data-testid="corporate-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${h.image})` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${T_STYLE.forest}f0, ${T_STYLE.forest}cc)` }} />
        {editing && (
          <button onClick={() => uploadImage("hero.image")} className="absolute top-4 right-4 z-30 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Cambia sfondo hero</button>
        )}
        <div className="relative max-w-6xl mx-auto px-4 md:px-14 py-16 md:py-32">
          <div className="inline-flex items-center gap-2 mb-6 md:mb-8 px-3 md:px-4 py-2 rounded-full" style={{ backgroundColor: "rgba(236,107,31,0.15)", border: `1px solid ${T_STYLE.gold}` }}>
            <Building2 className="w-4 h-4" style={{ color: T_STYLE.goldSoft }} />
            <IE editing={editing} value={h.eyebrow} onChange={(v) => set("hero.eyebrow", v)} as="span" className="text-[10px] md:text-xs tracking-[2px] md:tracking-[3px] uppercase font-semibold" style={{ color: T_STYLE.goldSoft }} />
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-light leading-[1.05] mb-6 md:mb-8 text-white" style={serif}>
            <IE editing={editing} value={h.title_line1} onChange={(v) => set("hero.title_line1", v)} /><br />
            <em style={{ color: T_STYLE.goldSoft, fontStyle: "italic" }}><IE editing={editing} value={h.title_line2} onChange={(v) => set("hero.title_line2", v)} /></em>
          </h1>
          <IE editing={editing} value={h.subtitle} onChange={(v) => set("hero.subtitle", v)} multiline as="p" className="text-white/85 text-base md:text-xl leading-relaxed max-w-2xl mb-8 md:mb-10 font-light block" />
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
            <button onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3.5 md:py-4 text-xs md:text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-2xl"
              style={{ backgroundColor: T_STYLE.gold, color: "#fff" }} data-testid="btn-parliamone">
              <IE editing={editing} value={h.cta_primary} onChange={(v) => set("hero.cta_primary", v)} /> <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#pacchetti"
              className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3.5 md:py-4 text-xs md:text-sm tracking-[2px] uppercase font-semibold border transition-colors hover:bg-white/10"
              style={{ borderColor: T_STYLE.cream, color: T_STYLE.cream }} data-testid="btn-see-pacchetti">
              <IE editing={editing} value={h.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} />
            </a>
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <section className="py-24 px-6 md:px-14" data-testid="benefici">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <IE editing={editing} value={b.eyebrow} onChange={(v) => set("benefici.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.gold }} />
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T_STYLE.forest, ...serif }}>
              <IE editing={editing} value={b.title_line1} onChange={(v) => set("benefici.title_line1", v)} />{" "}
              <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}><IE editing={editing} value={b.title_line2} onChange={(v) => set("benefici.title_line2", v)} /></em>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="grid sm:grid-cols-2 gap-4">
              {(b.items || []).map((it, i) => {
                const Icon = ICON_MAP[it.icon] || Sparkles;
                return (
                  <div key={i} className="p-6 bg-white border transition-shadow hover:shadow-xl" style={{ borderColor: T_STYLE.line }} data-testid={`beneficio-${i}`}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${T_STYLE.gold}20` }}>
                      <Icon className="w-5 h-5" style={{ color: T_STYLE.gold }} />
                    </div>
                    <IE editing={editing} value={it.testo} onChange={(v) => set(`benefici.items[${i}].testo`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T_STYLE.softInk }} />
                  </div>
                );
              })}
            </div>
            <div className="relative">
              <img src={b.image} alt="Team felice" className="w-full h-[500px] object-cover shadow-2xl" />
              {editing && (
                <button onClick={() => uploadImage("benefici.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Cambia immagine</button>
              )}
              <div className="absolute -bottom-6 -left-6 h-24 w-24" style={{ backgroundColor: T_STYLE.gold }} />
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILASTRI */}
      <section className="py-24 px-6 md:px-14" style={{ backgroundColor: "#EBE4D6" }} data-testid="pilastri">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <IE editing={editing} value={pil.eyebrow} onChange={(v) => set("pilastri.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.gold }} />
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T_STYLE.forest, ...serif }}>
              <IE editing={editing} value={pil.title_line1} onChange={(v) => set("pilastri.title_line1", v)} />{" "}
              <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}><IE editing={editing} value={pil.title_line2} onChange={(v) => set("pilastri.title_line2", v)} /></em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(pil.items || []).map((p, i) => {
              const Icon = ICON_MAP[p.icon] || Activity;
              return (
                <div key={i} className="p-10 bg-white text-center shadow-lg" data-testid={`pilastro-${i}`}>
                  <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: T_STYLE.forest }}>
                    <Icon className="w-9 h-9" style={{ color: T_STYLE.goldSoft }} />
                  </div>
                  <IE editing={editing} value={p.titolo} onChange={(v) => set(`pilastri.items[${i}].titolo`, v)} as="h3" className="text-2xl mb-4 font-light block" style={{ color: T_STYLE.forest, ...serif }} />
                  <IE editing={editing} value={p.desc} onChange={(v) => set(`pilastri.items[${i}].desc`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T_STYLE.softInk }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NATURA */}
      <section className="py-24 px-6 md:px-14" data-testid="natura">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 md:order-1">
            <img src={nat.image} alt="Movimento in natura" className="w-full h-[520px] object-cover shadow-2xl" />
            {editing && (
              <button onClick={() => uploadImage("natura.image")} className="absolute top-3 right-3 text-xs px-3 py-2 rounded-full font-semibold" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Cambia immagine</button>
            )}
          </div>
          <div className="order-1 md:order-2">
            <IE editing={editing} value={nat.eyebrow} onChange={(v) => set("natura.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.gold }} />
            <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: T_STYLE.forest, ...serif }}>
              <IE editing={editing} value={nat.title_line1} onChange={(v) => set("natura.title_line1", v)} /><br />
              <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}><IE editing={editing} value={nat.title_line2} onChange={(v) => set("natura.title_line2", v)} /></em>
            </h2>
            <IE editing={editing} value={nat.desc} onChange={(v) => set("natura.desc", v)} multiline as="p" className="text-base leading-relaxed" style={{ color: T_STYLE.softInk }} />
          </div>
        </div>
      </section>

      {/* 3 FASI */}
      <section className="py-24 px-6 md:px-14" style={{ backgroundColor: T_STYLE.forest }} data-testid="fasi">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <IE editing={editing} value={fs.eyebrow} onChange={(v) => set("fasi.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.goldSoft }} />
            <h2 className="text-4xl md:text-5xl font-light text-white" style={serif}>
              <IE editing={editing} value={fs.title_line1} onChange={(v) => set("fasi.title_line1", v)} />{" "}
              <em style={{ fontStyle: "italic", color: T_STYLE.goldSoft }}><IE editing={editing} value={fs.title_line2} onChange={(v) => set("fasi.title_line2", v)} /></em>
            </h2>
            <IE editing={editing} value={fs.note} onChange={(v) => set("fasi.note", v)} as="p" className="mt-4 text-sm text-white/60 block" />
          </div>
          <div className="space-y-6">
            {(fs.items || []).map((f, i) => (
              <div key={i} className="flex gap-8 p-8 bg-white/5 backdrop-blur-sm border transition-shadow hover:shadow-2xl" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid={`fase-${i}`}>
                <IE editing={editing} value={f.num} onChange={(v) => set(`fasi.items[${i}].num`, v)} as="div" className="text-6xl font-light" style={{ color: T_STYLE.goldSoft, ...serif }} />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <IE editing={editing} value={f.titolo} onChange={(v) => set(`fasi.items[${i}].titolo`, v)} as="h3" className="text-2xl font-light text-white block" style={serif} />
                    <IE editing={editing} value={f.tag} onChange={(v) => set(`fasi.items[${i}].tag`, v)} as="span" className="text-xs px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: `${T_STYLE.gold}30`, color: T_STYLE.goldSoft }} />
                  </div>
                  <IE editing={editing} value={f.desc} onChange={(v) => set(`fasi.items[${i}].desc`, v)} multiline as="p" className="text-white/75" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COACH */}
      <section className="py-24 px-6 md:px-14" style={{ backgroundColor: T_STYLE.cream }} data-testid="coach">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <IE editing={editing} value={ch.eyebrow} onChange={(v) => set("coach.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.gold }} />
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T_STYLE.forest, ...serif }}>
              <IE editing={editing} value={ch.title_line1} onChange={(v) => set("coach.title_line1", v)} />{" "}
              <em style={{ fontStyle: "italic", color: T_STYLE.gold }}><IE editing={editing} value={ch.title_line2} onChange={(v) => set("coach.title_line2", v)} /></em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(ch.items || []).map((c, i) => {
              const imgSrc = c.img && c.img.startsWith("/api/") ? `${API_ORIGIN}${c.img}` : c.img;
              return (
                <div key={i} className="text-center relative" data-testid={`coach-${i}`}>
                  <div className="relative mx-auto mb-6 w-56 h-56">
                    <div className="w-full h-full rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 4px ${T_STYLE.gold}` }}>
                      <img src={imgSrc} alt={c.nome} className="w-full h-full object-cover" onError={(e) => { e.target.style.backgroundColor = "#eee"; }} />
                    </div>
                    {editing && (
                      <button
                        type="button"
                        onClick={() => uploadImage(`coach.items[${i}].img`)}
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full font-semibold shadow-lg"
                        style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}
                        data-testid={`edit-coach-img-${i}`}
                      >
                        📷 Cambia foto
                      </button>
                    )}
                  </div>
                  <IE editing={editing} value={c.nome} onChange={(v) => set(`coach.items[${i}].nome`, v)} as="h3" className="text-2xl mb-2 font-light block" style={{ color: T_STYLE.forest, ...serif }} />
                  <IE editing={editing} value={c.ruolo} onChange={(v) => set(`coach.items[${i}].ruolo`, v)} as="p" className="text-xs uppercase tracking-[3px] mb-4 font-semibold block" style={{ color: T_STYLE.gold }} />
                  <IE editing={editing} value={c.bio} onChange={(v) => set(`coach.items[${i}].bio`, v)} multiline as="p" className="text-sm leading-relaxed" style={{ color: T_STYLE.softInk }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PACCHETTI */}
      <section id="pacchetti" className="py-24 px-6 md:px-14" style={{ backgroundColor: "#EBE4D6" }} data-testid="pacchetti">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <IE editing={editing} value={pk.eyebrow} onChange={(v) => set("pacchetti.eyebrow", v)} as="p" className="text-xs tracking-[6px] uppercase mb-4 block" style={{ color: T_STYLE.gold }} />
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T_STYLE.forest, ...serif }}>
              <IE editing={editing} value={pk.title_line1} onChange={(v) => set("pacchetti.title_line1", v)} />{" "}
              <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}><IE editing={editing} value={pk.title_line2} onChange={(v) => set("pacchetti.title_line2", v)} /></em>
            </h2>
            <IE editing={editing} value={pk.subtitle} onChange={(v) => set("pacchetti.subtitle", v)} multiline as="p" className="mt-4 text-sm max-w-2xl mx-auto block" style={{ color: T_STYLE.softInk }} />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(pk.items || []).map((p, i) => (
              <div key={i}
                className={`relative p-8 border-2 transition-all ${p.highlight ? "scale-[1.03] shadow-2xl" : "hover:shadow-xl"}`}
                style={{ borderColor: p.highlight ? T_STYLE.gold : T_STYLE.line, backgroundColor: p.highlight ? T_STYLE.forest : "#fff", color: p.highlight ? T_STYLE.cream : T_STYLE.ink }}
                data-testid={`pacchetto-${i}`}>
                {p.highlight && (<div className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold tracking-[2px] uppercase px-4 py-1" style={{ backgroundColor: T_STYLE.gold, color: "#fff" }}>Più richiesto</div>)}
                <IE editing={editing} value={p.nome} onChange={(v) => set(`pacchetti.items[${i}].nome`, v)} as="h3" className="text-3xl mb-1 font-light block" style={serif} />
                <IE editing={editing} value={p.partecipanti} onChange={(v) => set(`pacchetti.items[${i}].partecipanti`, v)} as="p" className="text-xs uppercase tracking-[2px] mb-2 font-semibold block" style={{ color: p.highlight ? T_STYLE.goldSoft : T_STYLE.gold }} />
                <IE editing={editing} value={p.ideale} onChange={(v) => set(`pacchetti.items[${i}].ideale`, v)} as="p" className="text-xs mb-6 opacity-70 block" />
                <ul className="space-y-3 mb-8">
                  {(p.features || []).map((f, j) => (
                    <li key={j} className="flex gap-3 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.highlight ? T_STYLE.goldSoft : T_STYLE.gold }} />
                      <IE editing={editing} value={f} onChange={(v) => set(`pacchetti.items[${i}].features[${j}]`, v)} className="flex-1" />
                    </li>
                  ))}
                </ul>
                <div className="pt-6 border-t" style={{ borderColor: p.highlight ? "rgba(245,241,235,0.2)" : T_STYLE.line }}>
                  <IE editing={editing} value={p.prezzo} onChange={(v) => set(`pacchetti.items[${i}].prezzo`, v)} as="p" className="text-sm font-semibold mb-4 block" />
                  <button onClick={() => setFormOpen(true)}
                    className="w-full py-3 text-xs tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02]"
                    style={{ backgroundColor: p.highlight ? T_STYLE.gold : "transparent", color: p.highlight ? "#fff" : T_STYLE.forest, border: p.highlight ? "none" : `1px solid ${T_STYLE.forest}` }}
                    data-testid={`btn-richiedi-${i}`}>
                    Richiedi preventivo
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-24 px-6 md:px-14" style={{ backgroundColor: T_STYLE.cream }} data-testid="cta-finale">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-6" style={{ color: T_STYLE.gold }} />
          <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: T_STYLE.forest, ...serif }}>
            <IE editing={editing} value={cta.title_line1} onChange={(v) => set("cta_finale.title_line1", v)} />{" "}
            <em style={{ fontStyle: "italic", color: T_STYLE.softInk }}><IE editing={editing} value={cta.title_line2} onChange={(v) => set("cta_finale.title_line2", v)} /></em>
          </h2>
          <IE editing={editing} value={cta.desc} onChange={(v) => set("cta_finale.desc", v)} multiline as="p" className="text-lg mb-10 max-w-2xl mx-auto block" style={{ color: T_STYLE.softInk }} />
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => setFormOpen(true)}
              className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold transition-all hover:scale-[1.02] shadow-xl"
              style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="cta-finale-btn">
              <IE editing={editing} value={cta.cta_primary} onChange={(v) => set("cta_finale.cta_primary", v)} /> <ArrowRight className="w-4 h-4" />
            </button>
            <a href={cta.brochure_url || "/longevity/presentazione-banche"}
              className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-semibold border transition-colors"
              style={{ borderColor: T_STYLE.forest, color: T_STYLE.forest }}>
              <Download className="w-4 h-4" /> Scarica Brochure PDF
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-6 md:px-14 text-center" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }}>
        <IE editing={editing} value={ft.brand} onChange={(v) => set("footer.brand", v)} as="p" className="text-xs tracking-[4px] uppercase mb-3 block" style={{ color: T_STYLE.goldSoft }} />
        <div className="flex flex-wrap justify-center gap-6 text-sm">
          <a href={`mailto:${ft.email || "info@longevityacademy.it"}`} className="inline-flex items-center gap-2 hover:opacity-80"><Mail className="w-4 h-4" /> {ft.email || "info@longevityacademy.it"}</a>
          <a href="/longevity" className="hover:opacity-80">← Torna a Longevity Academy</a>
        </div>
      </footer>

      {/* CONTACT FORM MODAL */}
      {formOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" style={{ backgroundColor: "rgba(15,15,15,0.85)" }} onClick={() => setFormOpen(false)} data-testid="contact-modal">
          <div className="bg-white max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setFormOpen(false)} className="absolute top-4 right-4 text-sm opacity-60 hover:opacity-100" data-testid="close-modal">✕</button>
            <h3 className="text-2xl font-light mb-2" style={{ color: T_STYLE.forest, ...serif }}>Richiedi un incontro</h3>
            <p className="text-sm mb-6" style={{ color: T_STYLE.softInk }}>Compilate il form: vi ricontattiamo entro 24h lavorative.</p>
            <form action={`mailto:${ft.email || "info@longevityacademy.it"}`} method="post" encType="text/plain" className="space-y-4">
              <input name="Azienda" placeholder="Nome azienda / organizzazione" required className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} data-testid="input-azienda" />
              <input name="Referente" placeholder="Nome referente" required className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} data-testid="input-referente" />
              <input name="Email" type="email" placeholder="Email di contatto" required className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} data-testid="input-email" />
              <input name="Telefono" placeholder="Telefono (opzionale)" className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} />
              <select name="Partecipanti" required className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} data-testid="select-partecipanti">
                <option value="">Numero collaboratori coinvolti</option>
                <option>10 - 50 (Starter)</option>
                <option>50 - 150 (Growth)</option>
                <option>150+ (Enterprise)</option>
              </select>
              <textarea name="Note" placeholder="Note / obiettivi" rows="3" className="w-full px-4 py-3 border text-sm" style={{ borderColor: T_STYLE.line }} />
              <button type="submit" className="w-full py-4 text-sm tracking-[2px] uppercase font-semibold" style={{ backgroundColor: T_STYLE.forest, color: T_STYLE.cream }} data-testid="submit-contact">
                Invia richiesta
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LongevityCorporatePage;
